import { isProblemDetails, type components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import { ApiProblem } from './me.ts';

// CB-REQ CRW-01, CRW-11 – CRW-13 at v1.960 – v1.961, crew slice part 1: the crew list, shifts on a
// job, and the roster across jobs. The office records offers and answers as they are phoned; the
// crew's own side is part 2.

export type CrewMember = components['schemas']['CrewMemberView'];
export type Named = components['schemas']['NamedView'];
/** A shift as any staff member sees it: `rate` is there only for someone with prices.view. */
export type Shift = components['schemas']['ShiftCostedView'];
export type Clash = components['schemas']['ShiftClashView'];
export type OfferOption = components['schemas']['OfferOptionView'];
export type CrewClass = CrewMember['class'];
export type ShiftState = Shift['state'];

export type CrewMemberInput = {
  displayName: string;
  email?: string | null;
  phone?: string | null;
  class: CrewClass;
  companyName?: string | null;
  notes?: string | null;
};

/**
 * CRW-11 at v1.996: someone's booked time — where they report (a venue or the depot, at most one;
 * neither is a held day), the job it is booked for, and who they ask on the day. A role is never
 * required; confirming needs a place, a dress code and a contact.
 */
export type ShiftInput = {
  /** CRW-02 at v1.9106: booked through the person's unavailability or leave, after the 409 said so. */
  throughTimeOff?: { reason?: string | null };
  venueId?: string | null;
  locationId?: string | null;
  bookedForJobId?: string | null;
  contactCrewMemberId?: string | null;
  roleId?: string | null;
  crewMemberId?: string | null;
  window: { start: string; end: string };
  callAt?: string | null;
  meetingPoint?: string | null;
  crewNotes?: string | null;
  officeNotes?: string | null;
  directToSite?: boolean;
  directFromSite?: boolean;
  dressCodeId?: string | null;
  rate?: { basis: 'hourly' | 'daily' | 'flat'; amountMinor: string } | null;
};

export const CREW_CLASS: Record<CrewClass, string> = {
  employee: 'Employee',
  contractor: 'Contractor',
  subcontractor: 'Subcontractor',
};

export const SHIFT_STATE: Record<ShiftState, string> = {
  draft: 'Draft',
  published: 'Published',
  offered: 'Offered',
  accepted: 'Accepted',
  declined: 'Declined',
  cancelled: 'Cancelled',
};

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

export const crewQuery = (includeRetired = false) =>
  queryOptions({
    queryKey: ['crew', { includeRetired }],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/crew', {
        params: { query: includeRetired ? { includeRetired: true } : {} },
      });
      if (error) throw problem(error, 'Could not load the crew list.');
      return data;
    },
  });

export async function createCrewMember(input: CrewMemberInput) {
  const { data, error } = await api.POST('/v1/crew', { body: input });
  if (error) throw problem(error, 'Could not add them.');
  return data;
}

export async function updateCrewMember(id: string, changes: Partial<CrewMemberInput>) {
  const { data, error } = await api.PATCH('/v1/crew/{id}', {
    params: { path: { id } },
    body: changes,
  });
  if (error) throw problem(error, 'Could not save that.');
  return data;
}

export async function retireCrewMember(id: string) {
  const { data, error } = await api.POST('/v1/crew/{id}/retire', { params: { path: { id } } });
  if (error) throw problem(error, 'Could not retire them.');
  return data;
}

export const rolesQuery = queryOptions({
  queryKey: ['crew-roles'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/crew-roles');
    if (error) throw problem(error, 'Could not load the roles.');
    return data;
  },
});

export async function addRole(name: string) {
  const { data, error } = await api.POST('/v1/crew-roles', { body: { name } });
  if (error) throw problem(error, 'Could not add that role.');
  return data;
}

export const dressCodesQuery = queryOptions({
  queryKey: ['dress-codes'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/dress-codes');
    if (error) throw problem(error, 'Could not load the dress codes.');
    return data;
  },
});

export async function addDressCode(name: string) {
  const { data, error } = await api.POST('/v1/dress-codes', { body: { name } });
  if (error) throw problem(error, 'Could not add that dress code.');
  return data;
}

export const jobShiftsQuery = (jobId: string) =>
  queryOptions({
    queryKey: ['jobs', jobId, 'shifts'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/jobs/{jobId}/shifts', {
        params: { path: { jobId } },
      });
      if (error) throw problem(error, 'Could not load the crew for this job.');
      return data as Shift[];
    },
  });

/** CRW-16 at v1.996: what confirming still needs, in the office's words. */
export const MISSING_TO_CONFIRM: Record<Shift['missingToConfirm'][number], string> = {
  place: 'Where',
  dress_code: 'Dress code',
  contact: 'Contact',
};

/** Where a shift reports, in a word or two: a venue, the depot, or a held day's "Venue to come". */
export function placeLabel(s: Pick<Shift, 'venue' | 'location'>): string {
  return s.venue?.name ?? s.location?.name ?? 'Venue to come';
}

/** Every live shift overlapping [from, to), across jobs. */
export const rosterQuery = (from: string, to: string) =>
  queryOptions({
    queryKey: ['roster', from, to],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/shifts', {
        params: { query: { from, to } },
      });
      if (error) throw problem(error, 'Could not load the roster.');
      return data as Shift[];
    },
  });

/** A draft: at a venue or the depot, booked for a job or none, or with no place a held day. */
export async function createShift(input: ShiftInput) {
  const { data, error } = await api.POST('/v1/shifts', { body: input });
  if (error) throw problem(error, 'Could not add that shift.');
  return data as Shift;
}

/** Any field, the place and the booked-for job included (CRW-11 at v1.996). */
export async function updateShift(id: string, changes: Partial<ShiftInput>) {
  const { data, error } = await api.PATCH('/v1/shifts/{id}', {
    params: { path: { id } },
    body: changes,
  });
  if (error) throw problem(error, 'Could not save that shift.');
  return data as Shift;
}

export async function removeShift(id: string) {
  const { error } = await api.DELETE('/v1/shifts/{id}', { params: { path: { id } } });
  if (error) throw problem(error, 'Could not remove that shift.');
}

export async function publishShifts(jobId: string, shiftIds: string[]) {
  const { data, error } = await api.POST('/v1/jobs/{jobId}/shifts/publish', {
    params: { path: { jobId } },
    body: { shiftIds },
  });
  if (error) throw problem(error, 'Could not publish those shifts.');
  return data as Shift[];
}

/** CRW-16 at v1.978: accepted shifts the office should have confirmed by now, soonest first. */
export const toConfirmQuery = queryOptions({
  queryKey: ['shifts', 'to-confirm'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/shifts/to-confirm');
    if (error) throw problem(error, 'Could not load the shifts to confirm.');
    return data as Shift[];
  },
});

// CRW-11 at v1.980: held days still without a job inside the assign-by window, and releasing one.
export const toAssignQuery = queryOptions({
  queryKey: ['shifts', 'to-assign'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/shifts/to-assign');
    if (error) throw problem(error, 'Could not load the held days to assign.');
    return data as Shift[];
  },
});

export async function releaseShift(id: string, reason: string) {
  const { data, error } = await api.POST('/v1/shifts/{id}/release', {
    params: { path: { id } },
    body: { reason },
  });
  if (error) throw problem(error, 'Could not release that held day.');
  return data as Shift;
}

/**
 * CRW-16 at v1.978 – v1.996: confirm these accepted shifts; ones already confirmed are left as they
 * are, and one lacking a place, dress code or contact refuses the batch, by name.
 */
export async function confirmShifts(shiftIds: string[]) {
  const { data, error } = await api.POST('/v1/shifts/confirm', { body: { shiftIds } });
  if (error) throw problem(error, 'Could not confirm those shifts.');
  return data as Shift[];
}

export const offerOptionsQuery = (shiftId: string) =>
  queryOptions({
    queryKey: ['shifts', shiftId, 'offer-options'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/shifts/{id}/offer-options', {
        params: { path: { id: shiftId } },
      });
      if (error) throw problem(error, 'Could not load who to offer it to.');
      return data;
    },
  });

export async function offerShift(
  id: string,
  crewMemberId: string,
  throughTimeOff?: { reason?: string | null },
) {
  const { data, error } = await api.POST('/v1/shifts/{id}/offer', {
    params: { path: { id } },
    body: { crewMemberId, ...(throughTimeOff ? { throughTimeOff } : {}) },
  });
  if (error) throw problem(error, 'Could not offer that shift.');
  return data as Shift;
}

export async function withdrawOffer(id: string) {
  const { data, error } = await api.POST('/v1/shifts/{id}/withdraw', { params: { path: { id } } });
  if (error) throw problem(error, 'Could not withdraw the offer.');
  return data as Shift;
}

export async function answerShift(
  id: string,
  answer: { answer: 'accepted' } | { answer: 'declined'; reason?: string | null },
) {
  const { data, error } = await api.POST('/v1/shifts/{id}/answer', {
    params: { path: { id } },
    body: answer,
  });
  if (error) throw problem(error, 'Could not record their answer.');
  return data as Shift;
}

export async function cancelShift(id: string) {
  const { data, error } = await api.POST('/v1/shifts/{id}/cancel', { params: { path: { id } } });
  if (error) throw problem(error, 'Could not cancel that shift.');
  return data as Shift;
}

/** "Also on J-1012 (offered)": a clash in the office's words. */
export function clashLabel(c: Clash): string {
  const where = c.bookedForJob
    ? `J-${c.bookedForJob.number}`
    : (c.venue?.name ?? c.location?.name ?? 'a held day');
  return `${where} (${SHIFT_STATE[c.state].toLowerCase()})`;
}

/**
 * Whether a clash is the kind that refuses (CRW-12): both this shift and the other one accepted is
 * impossible, so an accepted clash on a shift not yet accepted means accepting this one will fail.
 */
export function clashBlocks(c: Clash): boolean {
  return c.state === 'accepted';
}

// — Crew part 2 (CB-REQ CRW-01, CRW-08, CRW-13, CRW-14 at v1.964) —

/** A crew member's own shift: no rate, office notes or other crew are on this type (INV-10). */
export type MyShift = components['schemas']['ShiftCrewView'];
export type ChangeRules = components['schemas']['ChangeRulesView'];

export const myShiftsQuery = queryOptions({
  queryKey: ['my-shifts'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/my/shifts');
    if (error) throw problem(error, 'Could not load your shifts.');
    return data;
  },
});

export async function acceptMyShift(id: string) {
  const { data, error } = await api.POST('/v1/my/shifts/{id}/accept', { params: { path: { id } } });
  if (error) throw problem(error, 'Could not accept that shift.');
  return data;
}

export async function declineMyShift(id: string, reason: string | null) {
  const { data, error } = await api.POST('/v1/my/shifts/{id}/decline', {
    params: { path: { id } },
    body: { reason },
  });
  if (error) throw problem(error, 'Could not decline that shift.');
  return data;
}

export async function acknowledgeMyShift(id: string) {
  const { data, error } = await api.POST('/v1/my/shifts/{id}/acknowledge', {
    params: { path: { id } },
  });
  if (error) throw problem(error, 'Could not confirm that shift.');
  return data;
}

/** CRW-14 at v1.975: Got it — they have seen what changed; their confirmation stands. */
/** OPS-02 at v1.996: Done on one of their own tasks. Returns the shift carrying it. */
export async function doneMyTask(id: string) {
  const { data, error } = await api.POST('/v1/my/tasks/{id}/done', { params: { path: { id } } });
  if (error) throw problem(error, 'Could not mark that done.');
  return data;
}

export async function seenMyShift(id: string) {
  const { data, error } = await api.POST('/v1/my/shifts/{id}/seen', {
    params: { path: { id } },
  });
  if (error) throw problem(error, 'Could not record that you’ve seen it.');
  return data;
}

// CRW-08 and CLI-07 at v1.979: phone sharing, set by the person or by the office with a note.
export type PhoneSharing = components['schemas']['PhoneSharingView'];

export const myPhoneSharingQuery = queryOptions({
  queryKey: ['my-phone-sharing'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/my/phone-sharing');
    if (error) throw problem(error, 'Could not load your phone setting.');
    return data;
  },
});

export async function setMyPhoneSharing(shared: boolean) {
  const { data, error } = await api.PUT('/v1/my/phone-sharing', { body: { shared } });
  if (error) throw problem(error, 'Could not save your phone setting.');
  return data;
}

export const crewPhoneSharingQuery = (id: string) =>
  queryOptions({
    queryKey: ['crew-phone-sharing', id],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/crew/{id}/phone-sharing', {
        params: { path: { id } },
      });
      if (error) throw problem(error, 'Could not load their phone setting.');
      return data;
    },
  });

export async function setCrewPhoneSharing(id: string, shared: boolean, note: string) {
  const { data, error } = await api.PUT('/v1/crew/{id}/phone-sharing', {
    params: { path: { id } },
    body: { shared, note },
  });
  if (error) throw problem(error, 'Could not save their phone setting.');
  return data;
}

/** ADR-0013: a passcode-only sign-in under the Crew role. Nothing is sent to them. */
export async function grantCrewSignIn(id: string) {
  const { data, error } = await api.POST('/v1/crew/{id}/sign-in', { params: { path: { id } } });
  if (error) throw problem(error, 'Could not give them sign-in.');
  return data;
}

export const changeRulesQuery = queryOptions({
  queryKey: ['crew-change-rules'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/crew/change-rules');
    if (error) throw problem(error, 'Could not load the change rules.');
    return data;
  },
});

export async function saveChangeRules(rules: ChangeRules) {
  const pair = (p: ChangeRules['call']) => ({
    reconfirmAfter: p.reconfirmAfter,
    reacceptAfter: p.reacceptAfter ?? null,
  });
  const body = {
    ...rules,
    startEarlier: pair(rules.startEarlier),
    startLater: pair(rules.startLater),
    endLater: pair(rules.endLater),
    endEarlier: pair(rules.endEarlier),
    call: pair(rules.call),
    officePhone: rules.officePhone ?? null,
  };
  const { data, error } = await api.PUT('/v1/crew/change-rules', { body });
  if (error) throw problem(error, 'Could not save the change rules.');
  return data;
}

export type ShiftChangePreview = components['schemas']['ShiftChangePreviewView'];

/**
 * OPS-03 at v1.9105 (crew part 4C-1): what moving the shift to these wall-clock times would do,
 * CRW-14's outcome and the tasks left outside, without saving.
 */
export async function previewShiftChange(
  id: string,
  window: { start: string; end: string },
  callAt?: string,
) {
  const { data, error } = await api.GET('/v1/shifts/{id}/change-preview', {
    params: {
      path: { id },
      query: { start: window.start, end: window.end, ...(callAt ? { call: callAt } : {}) },
    },
  });
  if (error) throw problem(error, 'Could not check that move.');
  return data;
}
